/** @jsx jsx */
import { jsx } from '@emotion/react';
import React, { useEffect, useState } from 'react';
import { Annotation, ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { useTooltip } from 'components/chartTooltip/useTooltip';
import ChartTooltip from 'components/chartTooltip';
import { legendStyles, mapStyles } from './styles';
import { primaryColor } from 'styles/colors';

const MapChart = ({ data, dimensions, shapes, max, dataMapper, projection, colorScale, center, legend }) => {
	const [toolTip, setupTrigger] = useTooltip();
	const [isTooltipEnabled, setIsTooltipEnabled] = useState(true);
	const [zoom, setZoom] = useState(1);
	const [animationClass, setAnimationClass] = useState(mapStyles.animation);

	const defaultColorScale = d3
		.scaleLinear()
		.domain([0, max])
		.range(['#FFFFFF', primaryColor]);

	// render the shapes in white if there's no data
	const fillColorScale = max ? (colorScale ? colorScale : defaultColorScale) : () => '#FFFFFF';
	const disableTooltip = () => setIsTooltipEnabled(false);
	const enableTooltip = () => setIsTooltipEnabled(true);
	const handleZoom = (e) => {
		if (e.deltaY < 0) {
			// zoom in
			setZoom(prev => prev + 0.25);
		} else if (e.deltaY > 0) {
			// zoom out -- prevent zooming out too far
			setZoom(prev => prev > 0.25 ? prev - 0.25 : prev);
		}
	};

	useEffect(() => {
		setAnimationClass(mapStyles.animation);
	}, [data]);

	return (
		<div css={mapStyles.container} style={{ height: dimensions.height, width: '100%' }} onMouseDown={disableTooltip}
			onMouseUp={enableTooltip} onWheel={handleZoom}>
			<div css={mapStyles.map}>
				<ComposableMap projection={projection} width={800} height={600} style={{ width: '100%', height: '100%' }}>
					<ZoomableGroup center={center} zoom={zoom} minZoom={0.25}>
						<Geographies geography={shapes}>
							{({ geographies }) => (
								<React.Fragment>
									{geographies.map(geo => {
										const current = dataMapper(geo);

										return (
											<Geography
												key={geo.rsmKey}
												geography={geo}
												cy-test='map-data-geo'
												css={animationClass}
												onMouseEnter={() => setAnimationClass('')}
												style={{
													default: {
														fill: fillColorScale(current),
														stroke: '#607D8B',
														strokeWidth: 0.75,
														outline: 'none',
													},
													hover: {
														fill: '#263238',
														stroke: '#607D8B',
														strokeWidth: 0.75,
														outline: 'none',
													},
													pressed: {
														fill: '#263238',
														stroke: '#607D8B',
														strokeWidth: 0.75,
														outline: 'none',
													},
												}}
												{...setupTrigger(<ChartTooltip title={geo.properties.name} content={{ Count: current }} />)}
											/>
										);
									})}
									{/* Renders DC label */}
									{geographies.filter(geo => geo.properties.name === 'District of Columbia').map(dc => {
										const count = dataMapper(dc);
										const fillColor = fillColorScale(count);

										return (
											<g key="dc-marker">
												<Annotation subject={d3.geoCentroid(dc)} dx={49} dy={21}>
													<rect
														width={20}
														height={20}
														fill={fillColor}
														stroke="black"
														strokeWidth={0.75}
														{...setupTrigger(<ChartTooltip title={dc.properties.name} content={{ Count: count }} />)} />
													<text x={22} y={15} fontSize={14}>DC</text>
												</Annotation>
											</g>
										);
									})}
								</React.Fragment>
							)}
						</Geographies>
					</ZoomableGroup>
				</ComposableMap>
			</div>
			{isTooltipEnabled && toolTip}
			{legend &&
				<div css={legendStyles.wrapper}>{legend}</div>
			}
		</div>
	);
};

export default MapChart;

MapChart.propTypes = {
	data: PropTypes.any,
	dimensions: PropTypes.any,
	paddingRange: PropTypes.array,
	colorScale: PropTypes.any,
	projection: PropTypes.string,
	shapes: PropTypes.any,
	max: PropTypes.number,
	dataMapper: PropTypes.any,
	center: PropTypes.any,
	legend: PropTypes.any,
};
